import {
  BrandfolderHexColors,
  FontIcons,
  Placements,
  StandardPopout,
  StandardPopoutProps
} from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, ReactNode, useContext } from 'react';
import styled, { css } from 'styled-components';

import { DragAndDropContext } from '@context/drag-and-drop';

import { AsideButton } from './aside-button';

type OmittedPopoutProps = Omit<
  StandardPopoutProps,
  | 'autoFocus'
  | 'children'
  | 'closeOnClickAway'
  | 'containerOffset'
  | 'focusLock'
  | 'labels'
  | 'placement'
  | 'showArrow'
  | 'triggerOffset'
  | 'width'
>;

export enum HoverHelpPositions {
  Both = 'both',
  Bottom = 'bottom',
  Top = 'top'
}

export interface AsidePopoutProps extends OmittedPopoutProps {
  content: ReactNode;
  icon: FontIcons;
  id: string;
  label: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  tooltip: string;
  hoverHelpPosition?: HoverHelpPositions;
}

const UnstyledAsidePopout: FunctionComponent<AsidePopoutProps> = (props) => {
  const {
    className,
    content,
    hoverHelpPosition,
    icon,
    id,
    label,
    open,
    setOpen,
    title,
    tooltip,
    ...otherProps
  } = props;

  const popoutId = `${id}-popout`;

  const { dragged } = useContext(DragAndDropContext);

  return (
    <StandardPopout
      {...otherProps}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      className={classNames({
        'bf-popout--hover-help-bottom':
          hoverHelpPosition === HoverHelpPositions.Both ||
          hoverHelpPosition === HoverHelpPositions.Bottom,

        'bf-popout--hover-help-top':
          hoverHelpPosition === HoverHelpPositions.Both ||
          hoverHelpPosition === HoverHelpPositions.Top,
        [`${className}`]: !!className
      })}
      closeOnClickAway
      containerOffset={0}
      content={content}
      focusLock
      id={popoutId}
      labels={{
        closeButtonLabel: 'Close',
        loaderLabel: 'Loading'
      }}
      open={open}
      placement={Placements.RightStart}
      popoutDialogProps={{
        style: {
          visibility: dragged ? 'hidden' : undefined
        }
      }}
      setOpen={setOpen}
      showArrow={false}
      title={title}
      triggerOffset={0}
      width={500}
    >
      <AsideButton
        aria-controls={popoutId}
        aria-expanded={open}
        aria-haspopup="dialog"
        icon={icon}
        label={label}
        tooltip={tooltip}
        open={open}
        setOpen={setOpen}
      />
    </StandardPopout>
  );
};

/**
 * http://apps.eky.hk/css-triangle-generator/
 */
const hoverHelp = css`
  border-color: transparent transparent transparent transparent;
  border-style: solid;
  content: '';
  display: block;
  height: 0;
  left: 0;
  position: absolute;
  width: 0;
  z-index: 1;
`;

const StyledAsidePopout = styled(UnstyledAsidePopout)`
  .bf-popout__trigger {
    position: relative;
  }

  &.bf-popout--open.bf-popout--hover-help-bottom {
    .bf-popout__trigger:before {
      ${hoverHelp}
      border-width: 0 45px 20px 0;
      top: 100%;
    }
  }

  &.bf-popout--open.bf-popout--hover-help-top {
    .bf-popout__trigger:after {
      ${hoverHelp}
      border-width: 0 0 20px 45px;
      bottom: 100%;
    }
  }

  > .bf-popout__dialog {
    padding-left: 10px;
    position: absolute !important;
    left: 55px !important;
    top: 0 !important;

    > .bf-popout__dialog-inner {
      background-color: ${BrandfolderHexColors.Marshmallow};
      border-radius: 0;
      height: calc(100vh - 78px);

      .has-scrolled & {
        height: 100vh;
      }
    }
  }
`;

export const AsidePopout: FunctionComponent<AsidePopoutProps> = (props) => {
  return <StyledAsidePopout {...props} />;
};
